import { isResource, resolveId } from '@medplum/core'
import type {
  Attachment,
  CodeableConcept,
  Communication,
  DocumentReference,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
  systemFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  COMMUNICATION_MEDIUM_SYSTEM,
  DESTINATION_FAX_NUMBER_EXTENSION,
  ELATION_NON_VISIT_NOTE_TYPE_SYSTEM,
  SINCH_FAX_ID_SYSTEM,
} from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import {
  COMMUNICATION_MEDIUMS,
  type CommunicationMedium,
  ParticipationModeSubType,
} from '#src/models/communications/constants.ts'
import { letterSentDate } from '#src/models/elation/letter.ts'
import { Signable } from '#src/models/signable.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

export class ExtendedCommunication extends Signable<Communication> {
  static readonly RESOURCE_TYPE = 'Communication'

  static isInstance(value: unknown): value is Communication {
    return isResource(value) && isOfType(value, 'Communication')
  }

  static byPartOf(partOf: Reference): QueryTypes {
    return {
      _sort: '-_lastUpdated',
      _count: '1000',
      'status:not': 'entered-in-error',
      'part-of': partOf,
    }
  }

  static faxCommunicationsQuery(
    participantReference: Reference<Patient>
  ): QueryTypes {
    return {
      ...this.baseSearchQuery(),
      subject: participantReference,
      medium: ParticipationModeSubType.FAX,
    }
  }

  constructor(
    data: Omit<
      Communication,
      'resourceType' | 'payload' | 'status' | 'category'
    > & {
      status?: Communication['status']
      category?: CodeableConcept
      message?: string
    }
  ) {
    const { status, sent, subject, recipient, category, message, ...rest } =
      data
    super({
      status: status ?? 'completed',
      sent: sent ?? now(),
      recipient,
      category: category ? [category] : undefined,
      subject,
      ...rest,
    })
    if (message !== undefined) {
      this.message = message
    }
  }

  get sinchFaxId(): string | undefined {
    return this.identifier(SINCH_FAX_ID_SYSTEM)?.value
  }

  set sinchFaxId(value: string | undefined) {
    if (!value) {
      this.removeIdentifier(SINCH_FAX_ID_SYSTEM)
    } else {
      this.setIdentifier(SINCH_FAX_ID_SYSTEM, value)
    }
  }

  // Per-fax override of the destination number. When set, the fax pipeline
  // sends here instead of the recipient's primaryFaxNumber.
  @Extension({ url: DESTINATION_FAX_NUMBER_EXTENSION, kind: 'valueString' })
  destinationFaxNumber?: string

  get sender(): Reference<Practitioner | Organization> | undefined {
    if (this.resource.sender) {
      invariant(
        isOfType(this.resource.sender, 'Practitioner') ||
          isOfType(this.resource.sender, 'Organization'),
        'Sender must be a Practitioner or Organization'
      )
    }
    return this.resource.sender
  }

  get practitionerSender(): Reference<Practitioner> | undefined {
    const sender = this.sender
    return isOfType(sender, 'Practitioner') ? sender : undefined
  }

  get recipient():
    | Reference<Patient | Practitioner | Organization>
    | undefined {
    return this.resource.recipient?.[0] as
      | Reference<Patient | Practitioner | Organization>
      | undefined
  }

  get subject(): Reference<Patient> | undefined {
    if (this.resource.subject) {
      invariant(
        isOfType(this.resource.subject, 'Patient'),
        'Subject must be a Patient'
      )
      return this.resource.subject
    }
    return undefined
  }

  set subject(subject: Reference<Patient> | undefined) {
    this.resource.subject = subject
  }

  get message(): string {
    return this.resource.payload?.[0]?.contentString ?? ''
  }

  set message(value: string) {
    const trimmedValue = value.trim()

    this.resource.payload = trimmedValue
      ? [{ contentString: trimmedValue }]
      : undefined
  }

  get payload(): Communication['payload'] {
    return this.resource.payload
  }

  addDocumentPayload(document: Reference<DocumentReference>): void {
    this.resource.payload ??= []
    this.resource.payload.push({ contentReference: document })
  }

  addAttachmentPayload(attachment: Attachment): void {
    this.resource.payload ??= []
    this.resource.payload.push({ contentAttachment: attachment })
  }

  addTextPayload(text: string): void {
    this.resource.payload ??= []
    this.resource.payload.push({ contentString: text })
  }

  get basedOn(): Reference[] | undefined {
    return this.resource.basedOn
  }

  set basedOn(basedOn: Reference[]) {
    this.resource.basedOn = basedOn
  }

  get sent(): string {
    if (!this.resource.sent) {
      console.warn('No sent date found for communication', this.resource)
    }
    return this.resource.sent ?? ''
  }

  get sentDate(): Date | undefined {
    return this.resource.sent ? new Date(this.resource.sent) : undefined
  }

  /**
   * Send date for faxes synced from an Elation letter, recovered from the
   * source letter in the external-resource-json extension. Lets the UI place a
   * fax even when `Communication.sent` is null (e.g. letters synced before
   * `sent` was populated from the letter date). Undefined for non-letter
   * communications (the extension JSON has no letter dates).
   */
  get elationLetterSentDate(): Date | undefined {
    if (!this.externalResourceJSON) {
      return undefined
    }
    try {
      const letter = JSON.parse(this.externalResourceJSON) as {
        delivery_date?: string | null
        sign_date?: string | null
        document_date?: string | null
      }
      const date = letterSentDate(letter)
      return date ? new Date(date) : undefined
    } catch {
      return undefined
    }
  }

  get categoryCodes(): string[] | undefined {
    return this.resource.category?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get isEditable(): boolean {
    return !this.resource.category?.some((c) =>
      c.coding?.some(
        (coding) => coding.system === ELATION_NON_VISIT_NOTE_TYPE_SYSTEM
      )
    )
  }

  get partOf(): Reference[] {
    return this.resource.partOf ?? []
  }

  set partOf(partOfReference: Reference | undefined) {
    if (!partOfReference) {
      this.resource.partOf = []
      return
    }
    const currentPartOf = this.partOf
    if (!currentPartOf.length) {
      this.resource.partOf = [partOfReference]
      return
    }
    if (
      !currentPartOf.some((ref) => ref.reference === partOfReference.reference)
    ) {
      this.resource.partOf = [...currentPartOf, partOfReference]
    }
  }

  get status(): Communication['status'] {
    return this.resource.status
  }

  set status(value: Communication['status']) {
    this.resource.status = value
  }

  get isDeleted(): boolean {
    return this.resource.status === 'entered-in-error'
  }

  markDeleted(): void {
    this.resource.status = 'entered-in-error'
  }

  restore(): void {
    this.resource.status = 'completed'
  }

  get statusReason(): string | undefined {
    return this.resource.statusReason?.text
  }

  set statusReason(value: string | undefined) {
    this.resource.statusReason = value ? { text: value } : undefined
  }

  get medium(): CommunicationMedium | undefined {
    return codeFromCodeableConcept(
      this.resource.medium,
      COMMUNICATION_MEDIUM_SYSTEM
    ) as CommunicationMedium | undefined
  }

  get mediumDisplay(): string | undefined {
    return displayFromCodeableConcept(
      this.resource.medium,
      COMMUNICATION_MEDIUM_SYSTEM
    )
  }

  set medium(mode: CommunicationMedium) {
    this.resource.medium = [
      ...(this.resource.medium?.filter(
        (c) => systemFromCodeableConcept(c) !== COMMUNICATION_MEDIUM_SYSTEM
      ) ?? []),
      COMMUNICATION_MEDIUMS[mode],
    ]
  }

  get isFax(): boolean {
    return (
      this.resource.medium?.some(
        (cc) => codeFromCodeableConcept(cc) === ParticipationModeSubType.FAX
      ) ?? false
    )
  }

  protected static baseSearchQuery(): QueryTypes {
    return {
      _sort: '-_lastUpdated',
      _count: '1000',
      'status:not': 'entered-in-error',
    }
  }

  protected getExportFields(): ExportFields {
    return {
      id: this.id,
      status: this.resource.status,
      message: this.message,
      sent: this.resource.sent,
      sentDate: this.sentDate,
      senderId: resolveId(this.sender),
      patientId: resolveId(this.subject),
      category: this.categoryCodes,
      priority: this.resource.priority,
      recipientIds: this.resource.recipient?.map(resolveId),
      partOf: this.partOf.map(resolveId),
    }
  }
}
